import { useEffect, useState } from 'react';
import useAuth from '@/hooks/useAuth';
import { AccountTypeEnum } from '@/context/types/Account';
import { canAccessBatchPayments } from '@/utils/canAccessBatchPayments';
import { Feature } from '@/config/featureFlags.definition';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { PaymentGroupEnum } from '../types/ManagePayments';
import { fetchPaymentsByGroup } from '../utils/fetchPaymentsByGroup';
import {
  PAYMENTS_APPROVAL_REQUIRED_EVENT,
  PAYMENTS_APPROVAL_REQUIRED_RESOLVED_EVENT
} from '../utils/approvalRequiredEvents';

// How often the nav badge re-checks the count while the app is open, as a fallback for anything
// the two events below don't cover (e.g. another approver's action landing outside this session)
// - same spirit as useInProgressGroupPolling's timer but much less time-sensitive so a much
// longer interval is fine.
const POLL_INTERVAL_MS = 60_000;

/**
 * Lightweight counterpart to useManagePayment's full page-level fetch, for the nav's
 * approval-required count badge - same fetch-and-bucket pipeline (fetchPaymentsByGroup), reduced
 * to the one number the badge needs. There's no lighter backend endpoint for this yet (no
 * counts/summary endpoint exists - see fetchPaymentsByGroup), so this pays for the same three
 * calls the Payments page itself makes.
 *
 * This hook has its own fetch, independent of useManagePayment's - the two don't share state, so
 * a plain re-render elsewhere never updates this count. Three things trigger a refresh:
 * - the periodic poll below
 * - PAYMENTS_APPROVAL_REQUIRED_EVENT, a full refetch fired when a new batch/scheduled-payment
 *   submission comes back requiring approval (see dispatchApprovalRequiredChanged's call sites)
 * - PAYMENTS_APPROVAL_REQUIRED_RESOLVED_EVENT, a cheap local decrement fired when the current user
 *   approves (finally) or rejects a payment themselves (see dispatchApprovalRequiredResolved's
 *   call sites in useScheduledPaymentAction) - no need to pay for a full refetch when the delta is
 *   already known to be exactly one.
 * Same window-event idiom as 'auth:refresh-failed' for both.
 *
 * The remaining gap is someone *else's* approve/reject landing outside this session, which only
 * the poll picks up - acceptable given the interval above.
 */
export const useApprovalRequiredCount = (): number => {
  const auth = useAuth();
  const [count, setCount] = useState(0);

  const accountId = auth?.accountId;
  const isBusinessAccount = auth?.accountType === AccountTypeEnum.BUSINESS;
  const businessId = auth?.business?.id;
  const hasBatchPaymentAccess = canAccessBatchPayments(
    auth?.accountType,
    auth?.business?.accessLevel
  );

  useEffect(() => {
    if (!accountId || !isFeatureEnabled(Feature.MANAGE_PAYMENT)) return;

    let cancelled = false;

    const refresh = () => {
      fetchPaymentsByGroup({ accountId, isBusinessAccount, businessId, hasBatchPaymentAccess })
        .then((grouped) => {
          if (!cancelled) setCount(grouped[PaymentGroupEnum.ACTION_REQUIRED].length);
        })
        .catch(() => {
          // Leave the last-known count in place rather than flashing the badge to 0 on a
          // transient failure.
        });
    };

    // Never below 0 - purely defensive against this firing more times than the last full refresh
    // actually counted (e.g. a stale/duplicate dispatch), so the badge can't go negative.
    const decrement = () => setCount((prev) => Math.max(0, prev - 1));

    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    window.addEventListener(PAYMENTS_APPROVAL_REQUIRED_EVENT, refresh);
    window.addEventListener(PAYMENTS_APPROVAL_REQUIRED_RESOLVED_EVENT, decrement);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener(PAYMENTS_APPROVAL_REQUIRED_EVENT, refresh);
      window.removeEventListener(PAYMENTS_APPROVAL_REQUIRED_RESOLVED_EVENT, decrement);
    };
  }, [accountId, isBusinessAccount, businessId, hasBatchPaymentAccess]);

  return count;
};
